<template>
  <div class="results-table" data-testid="http-results-table">
    <div class="table-header">
      <h3 data-testid="http-results-count">Check Results ({{ results.length }})</h3>
      <export-button v-if="showExport && results.length > 0" :results="results" data-testid="http-export-button" />
    </div>
    
    <a-table
      :columns="columns"
      :data-source="results"
      :pagination="{ pageSize: 10 }"
      :row-key="getRowKey"
      :loading="loading"
      :scroll="{ x: 1200 }"
      data-testid="http-results-data-table"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'url'">
          <a :href="record.url" target="_blank" rel="noopener noreferrer">
            {{ truncateUrl(record.url) }}
          </a>
        </template>
        
        <template v-else-if="column.key === 'status_code'">
          <a-tag :color="getStatusColor(record.status_code)">
            {{ record.status_code }}
          </a-tag>
        </template>
        
        <template v-else-if="column.key === 'response_time'">
          {{ record.response_time.toFixed(3) }}s
        </template>
        
        <template v-else-if="column.key === 'redirect_chain'">
          <a-button
            v-if="record.redirect_chain.length > 1"
            size="small"
            @click="showRedirectChain(record)"
          >
            {{ record.redirect_chain.length }} redirects
          </a-button>
          <span v-else>-</span>
        </template>
        
        <template v-else-if="column.key === 'final_url'">
          <a
            v-if="record.final_url !== record.url"
            :href="record.final_url"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ truncateUrl(record.final_url) }}
          </a>
          <span v-else>-</span>
        </template>
        
        <template v-else-if="column.key === 'error'">
          <a-tooltip v-if="record.error" :title="record.error">
            <exclamation-circle-outlined style="color: #ff4d4f;" />
          </a-tooltip>
          <check-circle-outlined v-else style="color: #52c41a;" />
        </template>
      </template>
    </a-table>
    
    <!-- Redirect Chain Modal -->
    <a-modal
      v-model:open="modalVisible"
      title="Redirect Chain"
      :footer="null"
      width="600px"
    >
      <redirect-chain v-if="selectedResult" :steps="selectedResult.redirect_chain" />
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { ExclamationCircleOutlined, CheckCircleOutlined } from '@ant-design/icons-vue'
import ExportButton from './ExportButton.vue'
import RedirectChain from './RedirectChain.vue'
import type { CheckResult } from '../types'

const getRowKey = (record: CheckResult) => record.url

interface Props {
  results: CheckResult[]
  loading?: boolean
  showExport?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  showExport: true
})

const columns = [
  {
    title: 'URL',
    dataIndex: 'url',
    key: 'url',
    width: 300,
    ellipsis: true
  },
  {
    title: 'Status',
    dataIndex: 'status_code',
    key: 'status_code',
    width: 100,
    align: 'center'
  },
  {
    title: 'Time',
    dataIndex: 'response_time',
    key: 'response_time',
    width: 100,
    align: 'center'
  },
  {
    title: 'Redirects',
    dataIndex: 'redirect_chain',
    key: 'redirect_chain',
    width: 120,
    align: 'center'
  },
  {
    title: 'Final URL',
    dataIndex: 'final_url',
    key: 'final_url',
    width: 300,
    ellipsis: true
  },
  {
    title: 'Status',
    dataIndex: 'error',
    key: 'error',
    width: 80,
    align: 'center'
  }
]

const modalVisible = ref(false)
const selectedResult = ref<CheckResult | null>(null)

const getStatusColor = (status: number): string => {
  if (status >= 200 && status < 300) return 'success'
  if (status >= 300 && status < 400) return 'warning'
  if (status >= 400 && status < 500) return 'error'
  if (status >= 500) return 'error'
  return 'default'
}

const truncateUrl = (url: string, maxLength: number = 50): string => {
  if (url.length <= maxLength) return url
  return url.substring(0, maxLength) + '...'
}

const showRedirectChain = (result: CheckResult) => {
  selectedResult.value = result
  modalVisible.value = true
}
</script>

<style scoped lang="scss">
.results-table {
  margin-top: 24px;
  
  .table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    
    h3 {
      margin: 0;
      font-size: 18px;
    }
  }
}
</style>
